import React from "react";
import classNames from "classnames";

interface TimePickerState {
  isFocused: boolean;
  hour: number;
  min: number;
  isAM: boolean;
}

interface TimePickerProps {
  interval?: number;
  startTime?: string;
  endTime?: string;
  defaultHour?: number;
  defaultMinutes?: number;
  defaultMeridiem?: string;
  externalFieldToStoreValue?: any;
  errorMessage?: string;
}

class TimePicker extends React.Component<TimePickerProps, TimePickerState> {
  wrap: Element = null;

  constructor(props: TimePickerProps) {
    super(props);
    const { defaultHour, defaultMinutes, defaultMeridiem } = props;

    this.state = {
      isFocused: false,
      hour: defaultMeridiem === "PM" ? Number(defaultHour) - 12 : Number(defaultHour),
      min: Number(defaultMinutes),
      isAM: defaultMeridiem === "AM",
    };
    this.updateExternalField();
    document.addEventListener("click", (e) => this.handleDocClick(e));
  }

  componentDidUpdate() {
    this.updateExternalField();
  }

  updateExternalField() {
    if (!this.props.externalFieldToStoreValue) {
      return;
    }
    const { hour, min, isAM } = this.state;
    const time = `${this.padLeft(hour, 2)}:${this.padLeft(min, 2)} ${isAM ? "AM" : "PM"}`;
    const isValid = this.isValidTime();

    if (isValid) {
      this.props.externalFieldToStoreValue.value = time;
    } else {
      this.props.externalFieldToStoreValue.value = "";
    }
  }

  padLeft(number: number | string, charLength: number = 2): string {
    let str = number.toString();

    if (str.length < charLength) {
      str = "0" + str;
      return this.padLeft(str, charLength);
    }

    return str;
  }

  handleDocClick(e: any) {
    if (!this.state.isFocused) return;

    if (!this.wrap.contains(e.target)) {
      this.hide();
    }
  }

  handleKeyDown(keyCode: number): void {
    if (keyCode === 9 || keyCode === 13) {
      this.hide();
    }
  }

  show(): void {
    this.setState({ isFocused: true });
  }

  hide(): void {
    this.setState({ isFocused: false });
  }

  changeHour(direction: -1 | 1): void {
    let hour = this.state.hour;
    let isAM = this.state.isAM;

    if (direction === 1) {
      hour = hour + 1;
      if (hour == 12) {
        isAM = !isAM;
      }
    }

    if (direction === -1) {
      hour = hour - 1;
      if (hour == 11) {
        isAM = !isAM;
      }
    }

    if (hour > 12) {
      hour = 1;
    }

    if (hour < 1) {
      hour = 12;
    }

    this.setState({ hour: hour, isAM: isAM });
  }

  changeMin(direction: -1 | 1): void {
    let { min, hour } = this.state;
    const interval = this.props.interval || 30;

    if (direction === -1) {
      min = min - interval;
    } else {
      min = min + interval;
    }

    if (min < 0) {
      min = 60 - interval;
      this.changeHour(-1);
    }

    if (min > 59) {
      min = 0;
      this.changeHour(1);
    }

    this.setState({ min: min });
  }

  changeMeridiem(meridiem: "ante" | "post"): void {
    this.setState({ isAM: meridiem === "ante" });
  }

  isValidTime(hour: number = this.state.hour, mins: number = this.state.min): boolean {
    const { startTime, endTime } = this.props;

    if (!startTime || !endTime) {
      return true;
    }
    const start = startTime.split(":").map((val) => parseInt(val));
    const end = endTime.split(":").map((val) => parseInt(val));

    //Offset for 24 hour
    let actualHour = hour;

    if (actualHour === 12) {
      actualHour = 0;
    }

    if (!this.state.isAM) {
      actualHour = actualHour + 12;
    }

    //Convert it to secs and work from that
    const selectionSecs = actualHour * 3600 + mins * 60;
    const startSecs = start[0] * 3600 + start[1] * 60;
    const endSecs = end[0] * 3600 + end[1] * 60;

    return selectionSecs >= startSecs && selectionSecs <= endSecs;
  }

  render() {
    const { isFocused, hour, min, isAM } = this.state;
    const { errorMessage } = this.props;
    const time = `${this.padLeft(hour, 2)}:${this.padLeft(min, 2)} ${isAM ? "AM" : "PM"}`;
    const isValid = this.isValidTime();
    const wrapClass = classNames({
      timepicker__fieldWrap: true,
      "is-focused": isFocused,
    });

    return (
      <div ref={(item) => (this.wrap = item)}>
        <div className={wrapClass}>
          <input
            id="preferredTime"
            className={`text timepickerfield ${isValid ? "" : "input-validation-error"}`}
            name="preferredTime"
            type="input"
            onChange={() => {}}
            onFocus={() => this.show()}
            // onBlur={e => this.blur(e)}
            onKeyDown={(e: any) => this.handleKeyDown(e.keyCode)}
            value={time}
            readOnly={true}
          />
          {!isValid && <span className="field-validation-error">{errorMessage}</span>}
        </div>

        {isFocused ? (
          <div className="timepicker__select">
            <div className="timepicker__field">
              <div className="timepicker__control timepicker__control--down" onClick={() => this.changeHour(-1)} />
              <div className="timepicker__control timepicker__control--up" onClick={() => this.changeHour(1)} />
              <div className="timepicker__val">{this.padLeft(hour, 2)}</div>
            </div>

            <div className="timepicker__sep">:</div>

            <div className="timepicker__field">
              <div className="timepicker__control timepicker__control--down" onClick={() => this.changeMin(-1)} />
              <div className="timepicker__control timepicker__control--up" onClick={() => this.changeMin(1)} />
              <div className="timepicker__val">{this.padLeft(min, 2)}</div>
            </div>

            <div className="timepicker__meridiem">
              <div className={`timepicker__am ${isAM ? "active" : ""}`} onClick={() => this.changeMeridiem("ante")}>
                AM
              </div>
              <div className={`timepicker__pm ${isAM ? "" : "active"}`} onClick={() => this.changeMeridiem("post")}>
                PM
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

export default TimePicker;
